import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Theme } from '@astryxdesign/core/theme';
import { ToastViewport } from '@astryxdesign/core/Toast';
import { LinkProvider } from '@astryxdesign/core/Link';
import { InternationalizationProvider } from '@astryxdesign/core/i18n';
import ruRU from '@astryxdesign/core/locales/ru-RU.json';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { RouterLink } from './RouterLink';
import { studioTheme } from './theme';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) => count < 2 && (error as { code?: string }).code === 'network',
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

interface AccentCtx {
  setAccent: (hex: string | undefined) => void;
}
const AccentContext = createContext<AccentCtx>({ setAccent: () => undefined });
export const useAccent = () => useContext(AccentContext);

export function Providers({ children }: { children: ReactNode }) {
  const [accent, setAccent] = useState<string | undefined>(undefined);
  const theme = useMemo(() => studioTheme(accent), [accent]);
  const ctx = useMemo(() => ({ setAccent }), []);
  return (
    <QueryClientProvider client={queryClient}>
      <Theme theme={theme} mode="dark">
        <InternationalizationProvider locale="ru-RU" messages={{ 'ru-RU': ruRU }}>
        <LinkProvider component={RouterLink}>
          <AccentContext.Provider value={ctx}>
            <ToastViewport position="topEnd" maxVisible={3}>
              {children}
            </ToastViewport>
          </AccentContext.Provider>
        </LinkProvider>
        </InternationalizationProvider>
      </Theme>
    </QueryClientProvider>
  );
}
